<template>
  <article class="modelentry" :class="{ '-disabled': disabled || !canOpen }" @contextmenu="onModelContextmenu($event)">
    <div class="modelentry__content">
      <template v-if="locationVisible">
        <router-link
          v-if="workspace"
          :to="`${workspaceBaseUrl}/${folderSlug}`"
          class="modelentry__locationlink"
          @click="$emit('location-change')"
        >
          › {{ workspace?.name }} ›
          {{ folder?.name }}
        </router-link>
        <router-link
          v-else-if="model.type == 'shared_model'"
          :to="{ name: 'shared-models' }"
          class="modelentry__locationlink"
          @click="$emit('location-change')"
        >
          › {{ $t("labels.sharedModels") }}
        </router-link>
      </template>

      <header class="modelentry__header">
        <h3 class="modelentry__title">
          {{ model.name }}
          <span v-if="isSimulationGame" class="modelentry__titlenotice">
            ({{ $t("words.businessGame", 1) }})
          </span>
          <span v-if="isLocked" class="modelentry__titlenotice">
            ({{ $t("labels.locked") }})
          </span>
        </h3>
      </header>

      <div class="modelentry__statusline">
        <!-- synchronization status -->
        <template v-if="showModifiedAt">
          <div class="modelentry__synchstatus">
            {{ modifiedAtText }}
          </div>
          <template v-if="SAVE_LOCATION === 'local'">
            <div>|</div>
            <div
              v-if="synchronizeEnabled && !readOnly"
              class="modelentry__synchstatus"
            >
              <div v-if="'lastSynch' in model && model.lastSynch == null">
                {{ $t("labels.notSynchronizedYet") }}
              </div>
              <div v-else>
                {{ $t("labels.synchronizedXAgo", [synchedDate]) }}
              </div>
            </div>
            <div
              v-else-if="!readOnly && !synchronizeEnabled"
              class="modelentry__synchstatus"
            >
              {{ $t("labels.synchronizationDeactivated") }}
            </div>
          </template>
        </template>

        <template v-if="publicationsCount > 0 || sharesCount > 0">
          <div>|</div>

          <ul class="modelentry__shareslist line gap-2">
            <li v-if="publicationsCount > 0">
              {{ $t("status.publishedCount", publicationsCount) }}
            </li>
            <li v-if="sharesCount > 0">
              {{ $t("status.sharedCount", sharesCount) }}
            </li>
          </ul>
        </template>
      </div>

      <!-- description -->
      <formatted-read-more-text v-if="descriptionVisible" :value="model.description" />

      <div class="modelentry__buttons">
        <router-link class="modelentry__editlink" :to="routeToView">
          <lucide-icon name="settings" class="modelentry__editlink__icon" />
          <span v-if="!readOnly">{{ $t("labels.manage") }}</span>
          <span v-if="readOnly">{{ $t("labels.info") }}</span>
        </router-link>

        <button
          v-if="readOnly && canFork"
          class="modelentry__editlink"
          type="button"
          @click="duplicateModel"
        >
          <lucide-icon name="copy" class="modelentry__editlink__icon" />
          <span>{{ $t("labels.copyToMyModels") }}</span>
        </button>
      </div>
    </div>
    <router-link
      class="modelentry__link"
      :to="routeToEditor"
      :target="openModelsInNewTab ? '_blank' : undefined"
    >
      <lucide-icon name="chevrons-right" class="modelentry__arrow" />
      <span>{{ $t("labels.open") }}</span>
    </router-link>

    <context-menu
      v-if="contextMenuState.isOpen"
      :x="contextMenuState.x"
      :y="contextMenuState.y"
      @close="contextMenuState.isOpen = false"
    >
      <router-link class="context-menu__entry" :to="routeToView">
        <span v-if="!readOnly">{{ $t("labels.manage") }}</span>
        <span v-if="readOnly">{{ $t("labels.info") }}</span>
      </router-link>
      <button
        v-if="canUpdate"
        class="context-menu__entry"
        type="button"
        @click="state.moving = true"
      >
        {{ $t("labels.move") }}
      </button>
      <button
        v-if="canLock"
        class="context-menu__entry"
        type="button"
        @click="tryToToggleLock()"
      >
        <template v-if="isLocked">
          {{ $t("actions.unlockModel") }}
        </template>
        <template v-else>
          {{ $t("actions.lockModel") }}
        </template>
      </button>
      <button
        v-if="canFork"
        class="context-menu__entry"
        type="button"
        @click="state.duplicating = true"
      >
        {{ $t("labels.duplicate") }}
      </button>
      <button
        v-if="canDownload"
        :disabled="downloading"
        type="button"
        class="context-menu__entry"
        @click="downloadModel()"
      >
        {{ $t("labels.download") }}
      </button>
      <button
        v-if="canDelete"
        type="button"
        class="context-menu__entry"
        @click="tryToDeleteModel()"
      >
        {{ $t("actions.delete") }}
      </button>
    </context-menu>

    <model-move-popup
      v-if="model.type == 'workspace_model'"
      :visible="state.moving"
      :model="model"
      @close="state.moving = false"
    />

    <model-duplicate-popup
      :visible="state.duplicating"
      :model="model"
      @close="state.duplicating = false"
    />
  </article>
</template>

<script setup lang="ts">
import { reactive, computed, useAttrs, ref } from "vue";
import { formatDate, formatDateRelative } from "@/i18n";
import { DateTime } from "luxon";
import { useI18n } from "vue-i18n";
import envOptions from "@/helpers/Env";

import LucideIcon from "@core/ui/LucideIcon.vue";
import FormattedReadMoreText from "@core/ui/FormattedReadMoreText.vue";
import ContextMenu from "@core/ui/ContextMenu.vue";
import ModelDuplicatePopup from "@manager/workspacemodels/ModelDuplicatePopup.vue";
import ModelMovePopup from "@manager/workspacemodels/ModelMovePopup.vue";

import { useNow } from "@core/composables/useNow";
import { showConfirm, showError } from "@core/ui/alert/alert";
import { downloadModelFile } from "@/api/SaveService";
import { useUser } from "@/api/user.store";
import { useModels } from "@/api/models.store";
import { useWorkspaces } from "@/api/workspaces.store";
import { usePublicModels } from "@/api/publicModels.store";
import { useSharedModels } from "@/api/sharedModels.store";

import type { Model } from "@/api/Model";
import type { LocalModelInfo } from "@/api/DB";
import type { SharedModel } from "@/api/SharedModel";
import type { PublicModel } from "@/api/PublicModel";
import type { ModelVersion } from "@/api/ModelVersion";

interface Props {
  model: Model | LocalModelInfo | SharedModel | PublicModel;

  permissions?: Model["permissions"];
  descriptionVisible?: boolean;
  showModifiedAt?: boolean;
  locationVisible?: boolean;
  disabled?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  permissions: undefined,
  descriptionVisible: true,
  showModifiedAt: true,
  locationVisible: false,
  disabled: false
});

const emit = defineEmits<{
  (e: "copy", model: Model | LocalModelInfo | SharedModel | PublicModel): void;
  (e: "location-change"): void;
}>();

const { t } = useI18n();
const now = useNow();
const attrs = useAttrs();

const {
  getModelData: getWorkspaceModelData,
  destroyModel,
  updateModel
} = useModels();
const { getPublicModelData } = usePublicModels();
const { getSharedModelData } = useSharedModels();
const { workspaces } = useWorkspaces();

const SAVE_LOCATION = envOptions.saveLocation;
const openModelsInNewTab = envOptions.openModelsInNewTab;

const state = reactive({
  // flags for popups:
  duplicating: false,
  moving: false
});

const contextMenuState = reactive({
  isOpen: false,
  x: 0,
  y: 0
});

// ==============================
// Computed properties

const synchedDate = computed(() => {
  if (!("lastSynch" in props.model)) {
    return "";
  }

  const dataSynchDate = DateTime.fromJSDate(
    new Date(props.model?.lastVersion?.lastSynch ?? 0)
  );
  const date = DateTime.fromJSDate(new Date(props.model.lastSynch || 0));
  const laterTime = DateTime.max(date, dataSynchDate);
  const minutesSinceSync = Math.abs(laterTime.diffNow("minutes").as("minutes"));
  // use "now" here for update:
  if (now.value == Infinity || minutesSinceSync < 1) {
    return t("messages.lessThenAMinuteAgo");
  }
  return formatDateRelative(laterTime.toJSDate(), {
    now: now.value // here to update value every 1 s.
  });
});

const sharesCount = computed(() => {
  if (!isModel(props.model)) return 0;
  return props.model?.shares?.length ?? props.model?.sharesCount ?? 0;
});

const publicationsCount = computed(() => {
  if (!isModel(props.model)) return 0;
  return (
    props.model?.publications?.length ?? props.model?.publicationsCount ?? 0
  );
});

const isLocked = computed(() => {
  return "isLocked" in props.model ? props.model.isLocked : false;
});

// permissions
const canOpen = computed(() => {
  return props.permissions?.canOpen ?? props.model.permissions?.canOpen;
});
const canFork = computed(() => {
  return (
    props.permissions?.canFork ?? props.model.permissions?.canFork !== false
  );
});
const canDownload = computed(() => {
  return props.permissions?.canDownload ?? props.model.permissions?.canDownload;
});
const canDelete = computed(() => {
  return props.permissions?.canDelete ?? props.model.permissions?.canDelete;
});
const canUpdate = computed(() => {
  return props.permissions?.canUpdate ?? props.model.permissions?.canUpdate;
});
const canLock = computed(() => {
  return props.permissions?.canLock ?? props.model.permissions?.canLock;
});

const modifiedAtText = computed(() => {
  if (!("modified" in props.model)) return "";
  if (!props.model.modified) return "";

  const lastFileModified = new Date(
    "lastVersion" in props.model ? props.model?.lastVersion?.created ?? 0 : 0
  );
  const lastModified = DateTime.max(
    DateTime.fromJSDate(new Date(props.model.modified)),
    DateTime.fromJSDate(lastFileModified)
  );
  const dateFormatted = formatDate(lastModified.toJSDate(), "ff");

  if (lastEditorVisible.value) {
    let editedBy: string | undefined = undefined;
    if ("lastVersion" in props.model) {
      editedBy = props.model?.lastVersion?.creator?.name;
    }
    if (!editedBy && "creator" in props.model) {
      editedBy = props.model?.creator?.name;
    }
    if (!editedBy && "author" in props.model) {
      editedBy = props.model?.author;
    }

    if (editedBy) {
      return capitalizeFirstLetter(
        t("labels.modifiedAtExactUser", {
          datetime: dateFormatted,
          username: editedBy
        })
      );
    }
  }

  return capitalizeFirstLetter(
    t("labels.modifiedAtExact", {
      datetime: dateFormatted
    })
  );
});

const isSimulationGame = computed(() => {
  if ("isSimulationGame" in props.model) return props.model.isSimulationGame;
  return false;
});

const readOnly = computed(() => {
  return props.model.type != "workspace_model";
});

const synchronizeEnabled = computed(() => {
  return readOnly.value ? false : useUser().syncEnabled.value || false;
});

const workspace = computed(() => {
  if ("workspace" in props.model) {
    return workspaces[props.model.workspace];
  }
  return null;
});

const workspaceBaseUrl = computed(() => {
  if (!workspace.value) return;

  if (workspace.value.isPersonal) {
    return "/models/my";
  } else {
    return `/models/${workspace.value.organizationSlug}/${workspace.value.slug}`;
  }
});

const lastEditorVisible = computed(() => {
  return workspace.value?.isPersonal == false;
});

const folder = computed(() => {
  if ("folder" in props.model) {
    const folderId = props.model.folder;
    const workspaceFolders = workspace.value?.folders ?? [];
    return workspaceFolders.find((f) => f.id == folderId);
  }
  return undefined;
});

const folderSlug = computed(() => {
  if ("folderSlug" in props.model) {
    return props.model.folderSlug;
  }
  return folder.value?.slug;
});

const routeToView = computed(() => {
  if (props.model.type == "shared_model") {
    return {
      name: "shared-model-info",
      params: { shareId: props.model.id }
    };
  } else if (props.model.type == "public_model") {
    return {
      name: "public-model-info",
      params: { publicationId: props.model.id }
    };
  } else {
    if (workspace.value?.isPersonal) {
      return {
        name: "personal-workspace-model-info",
        params: {
          modelId: props.model.id,
          folderSlug: folderSlug.value
        }
      };
    }
    return {
      name: "team-model-info",
      params: {
        modelId: props.model.id,
        organizationSlug: workspace.value?.organizationSlug,
        teamSlug: workspace.value?.slug,
        folderSlug: folderSlug.value
      }
    };
  }
});
const routeToEditor = computed(() => {
  if (props.model.type == "shared_model") {
    return {
      name: "shared-model",
      params: { shareId: props.model.id }
    };
  } else if (props.model.type == "public_model") {
    return {
      name: "public-model",
      params: { publicationId: props.model.id }
    };
  } else {
    if (workspace.value?.isPersonal) {
      return {
        name: "personal-workspace-model",
        params: {
          modelId: props.model.id,
          folderSlug: props.model.folderSlug
        }
      };
    }
    return {
      name: "team-model",
      params: {
        modelId: props.model.id,
        organizationSlug: workspace.value?.organizationSlug,
        teamSlug: workspace.value?.slug,
        folderSlug: props.model.folderSlug
      }
    };
  }
});

// ==============================
// DOWNLOAD

const downloading = ref(false);

async function downloadModel(name: string | null = null) {
  if (downloading.value) return;

  try {
    downloading.value = true;
    const modelData = await getModelData();
  
    if (modelData == null || modelData.data == null) {
      showError(t("errors.noModeldata"));
      return;
    }
  
    return downloadModelFile(props.model, modelData, {
      name: name || props.model.name,
      isSimulationGame: isSimulationGame.value
    });
  } catch (error) {
    showError(error as Error);
  } finally {
    downloading.value = false;
  }
}

/**
 * Get the correct model data depending on the model type
 */
 function getModelData(): Promise<Partial<ModelVersion>> {
  if (isSharedModel(props.model)) {
    return getSharedModelData(props.model.id);
  }
  if (isPublicModel(props.model)) {
    return getPublicModelData(props.model.id);
  }

  return getWorkspaceModelData(props.model.workspace, props.model.id);
}

// ==============================
// Methods

async function duplicateModel() {
  emit("copy", props.model);
}

function onModelContextmenu(pointerEvent: MouseEvent) {
  if ("onContextmenu" in attrs) return;
  pointerEvent.preventDefault();
  contextMenuState.isOpen = true;
  contextMenuState.x = pointerEvent.clientX;
  contextMenuState.y = pointerEvent.clientY;
}

async function tryToDeleteModel() {
  if (!isModel(props.model)) return;

  if (
    await showConfirm(
      t("questions.confirmDeleteModel"),
      t("actions.deleteModel")
    )
  ) {
    try {
      await destroyModel(props.model);
    } catch (_error) {
      const error = _error as Error;
      console.error(error);
      if (error?.message) {
        showError(error);
      }
    }
  }
}

async function tryToToggleLock() {
  if (!isModel(props.model)) return;
  try {
    return await updateModel({
      id: props.model.id,
      workspace: props.model.workspace,
      isLocked: !props.model.isLocked
    });
  } catch (error) {
    showError(error as Error);
  }
}

// ==============================
// Helper functions

function isModel(
  model: Model | LocalModelInfo | SharedModel | PublicModel
): model is Model | LocalModelInfo {
  return model.type == "workspace_model";
}
function isSharedModel(
  model: Model | LocalModelInfo | SharedModel | PublicModel
): model is SharedModel {
  return model.type == "shared_model";
}
function isPublicModel(
  model: Model | LocalModelInfo | SharedModel | PublicModel
): model is PublicModel {
  return model.type == "public_model";
}

function capitalizeFirstLetter(word: string) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}
</script>

<style lang="scss">
.modelentry {
  box-shadow: 0 1px 5px var(--color-shadow-2);
  border-radius: 5px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) min-content;
  background-color: var(--color-background-close);
  min-width: 290px;

  &.-disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 0.25rem;
  }

  &__locationlink {
    position: relative;
    top: -0.25rem;
    display: inline-block;
    margin-bottom: 0.75rem;
    margin-left: -0.5rem;
    font-weight: 500;
    color: var(--color-text-medium);
    background-color: hsla(var(--color-primary-h), 80%, 50%, 0.1);
    padding: 0.25rem 0.5rem;
    border-radius: 9rem;
    box-shadow: 0 0 1px transparent;
    transition: box-shadow 150ms, background-color 150ms, color 150ms;

    &:hover {
      color: var(--color-action);
      box-shadow: 0 1px 2px var(--color-shadow-2);
    }
    &:focus-visible {
      outline: 2px solid var(--color-action);
      outline-offset: 5px;
    }
    &:active {
      box-shadow: 0 0 1px transparent;
      background-color: var(--color-action);
      color: var(--color-action-contrast);
    }
  }

  &__content,
  &__link {
    padding: 1.5rem;
  }

  &__title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }

  &__titlenotice {
    color: var(--color-text-light);
    font-weight: 600;
  }

  &__statusline {
    display: flex;
    gap: 0.5rem;
    font-size: 13px;
    color: var(--color-text-light);
    row-gap: 2px;
    flex-wrap: wrap;
  }

  &__shareslist {
    padding: 0;
    list-style: none;
    margin: 0;
    gap: 0.125rem;
    li {
      display: inline;
    }
    li::after {
      content: ",";
    }
    li:last-child::after {
      content: "";
    }
  }

  &__content {
    overflow: hidden; // prevents long descriptions from overflowing the text area
  }

  &__link {
    background-color: var(--color-background-close);
    color: var(--color-action);
    text-transform: uppercase;
    text-align: center;
    border-left: 1px solid var(--color-shadow-1);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    min-width: 10.5rem;

    padding-left: 3rem;
    padding-right: 3rem;

    border-radius: inherit;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;

    transition:
      text-shadow 250ms,
      box-shadow 250ms,
      font-weight 100ms;
    cursor: pointer;

    &:hover {
      font-weight: 700;
    }
    &:active {
      transition-duration: 100ms;
      box-shadow: inset 0 5px 5px -5px var(--color-shadow-1);
    }
    &:hover:not(:active) {
      box-shadow: inset 0 -5px 5px -5px var(--color-shadow-1);
      background-color: var(--color-background-close-hover);
    }
    &:focus-visible {
      outline: 2px solid var(--color-action);
      border-radius: 5px;
    }
  }

  &__arrow {
    font-size: 2.5rem;
    display: inline-block;
    margin-bottom: 0.25rem;
    stroke-width: 2.5px !important;
  }

  &__editlink {
    color: var(--color-action);
    text-transform: uppercase;
    background-color: transparent;
    border: none;
    font-family: var(--font-family-ui);
    display: flex;
    align-items: center;
    padding: 0;
    font-weight: 600;
    appearance: none;
    font-size: 14px;

    &:focus {
      outline: none;
    }
    &:focus-visible {
      outline: 2px solid var(--color-action);
      border-radius: 5px;
      outline-offset: 5px;
    }

    &__icon {
      margin-right: 0.35em;
      font-size: 14px;
    }
  }

  &__buttons {
    display: flex;
    margin-top: 2.5rem;
    gap: 1.5rem;
    align-items: center;
  }
 
  @container (width < 28rem) {
    grid-template-columns: minmax(0, 1fr);

    &__link {
      border-left: none;
      border-top: 1px solid var(--color-shadow-1);
      padding: 1rem;
      flex-direction: row-reverse;
      gap: 0.5rem;
    }
    &__arrow {
      font-size: 2rem;
      margin-bottom: 0;
    }
  }
}
</style>
